import React, { useState, useMemo } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@epoch-ui/components/Modal/modal';
import {
  ActionButtonGroup,
  ActionButtonGroupItem,
  ActionButtonGroupItemIcon,
  ActionButtonGroupItemLabel,
  ActionButtonGroupItemCaption
} from '@epoch-ui/components/ActionButtonGroup';
import { GuestListPrivacyEnum } from 'types/event.types';
import { useEditableEvent } from 'pages/dashboard/overview/events/editor/hooks/useEditableEvent';

interface GuestListPrivacyModalProps extends React.ComponentProps<typeof Modal> {
  onSave: (value?: GuestListPrivacyEnum) => void;
  initialValue?: GuestListPrivacyEnum;
}

const GuestListPrivacyModal = ({
  onSave,
  initialValue,
  onOpenChange,
  ...props
}: GuestListPrivacyModalProps): React.ReactElement => {
  const [value, setValue] = useState(initialValue);
  const event = useEditableEvent();

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      setValue(initialValue);
    }
    onOpenChange?.(open);
  };

  const isPrivateEvent = event?.isPrivate;

  const options = useMemo(
    () => [
      {
        value: GuestListPrivacyEnum.VISIBLE,
        icon: <EyeIcon />,
        label: 'Visible',
        caption: isPrivateEvent
          ? 'Guest list can be seen by other guests'
          : 'Guest list can be seen by anyone'
      },
      {
        value: GuestListPrivacyEnum.HIDDEN,
        icon: <EyeOffIcon />,
        label: 'Hidden',
        caption: 'Guest list is hidden'
      }
    ],
    [isPrivateEvent]
  );

  return (
    <Modal {...props} onOpenChange={handleOpenChange} modal>
      <ModalContent>
        <ModalHeader className="tw-mb-5">
          <ModalTitle>Guest list privacy</ModalTitle>
        </ModalHeader>
        <div className="tw-flex tw-flex-col tw-gap-5">
          <ActionButtonGroup
            value={value}
            onValueChange={(v): void => setValue(v as GuestListPrivacyEnum)}
          >
            {options.map((option) => (
              <ActionButtonGroupItem value={option.value} key={option.value}>
                <ActionButtonGroupItemIcon>{option.icon}</ActionButtonGroupItemIcon>
                <ActionButtonGroupItemLabel>
                  {option.label}
                  <ActionButtonGroupItemCaption>{option.caption}</ActionButtonGroupItemCaption>
                </ActionButtonGroupItemLabel>
              </ActionButtonGroupItem>
            ))}
          </ActionButtonGroup>
          <Button disabled={!value || value === initialValue} onClick={(): void => onSave(value)}>
            Save
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export { GuestListPrivacyModal };
